import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { IControlledMutator, Nullable, TriggeredHook } from "sonobello.utilities.react";

import INoteContentBuilder from "../types/INoteContentBuilder";

export interface INoteUpdaterConfig<TNoteProps> {
  /** The note content builder used to generate the note content. */
  noteContentBuilder: INoteContentBuilder<TNoteProps>;
  /** A triggered hook for dispatching note updates to an external service. */
  useDispatchNoteUpdate: TriggeredHook<unknown, string>;
}

export interface INoteUpdaterProps<TNoteProps> {
  /** The properties needed by the note content builder. */
  noteProps: TNoteProps;
  /** A state that triggers the note update when changed. */
  eventTrigger: unknown;
}

/** Maintains the content of the patient note, and dispatches changes to the updater hook when the page location changes or a timeout occurs. */
const OnLocationChangeNoteUpdater = <TNoteProps,>({
  eventTrigger,
  value,
  noteProps,
  noteContentBuilder,
  onChange,
  useDispatchNoteUpdate
}: INoteUpdaterProps<TNoteProps> & INoteUpdaterConfig<TNoteProps> & IControlledMutator<Nullable<string>>) => {
  const location = useLocation();
  const { execute } = useDispatchNoteUpdate();

  const currentNoteContent = React.useRef("");
  const lastSentNoteContent = React.useRef<string>(value || "");

  useEffect(() => {
    currentNoteContent.current = noteContentBuilder.buildNoteContent(noteProps);
  }, [noteProps]);

  useEffect(() => {
    return () => {
      if (currentNoteContent.current && currentNoteContent.current !== lastSentNoteContent.current) {
        execute(currentNoteContent.current);
        onChange(currentNoteContent.current);
        lastSentNoteContent.current = currentNoteContent.current;
      }
    };
  }, [location.pathname, eventTrigger]);

  return <React.Fragment />;
};

export default OnLocationChangeNoteUpdater;
